import { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  useCreateEmployeeProfileBlockField,
  useDeleteEmployeeProfileBlockField,
  useEmployeeProfileBlockFields,
  useReorderEmployeeProfileBlockFields,
  useUpdateEmployeeProfileBlockField,
} from '@/hooks/use-employee-profile-block-fields';
import type {
  CreateEmployeeProfileBlockFieldInput,
  EmployeeProfileBlock,
  EmployeeProfileBlockField,
  EmployeeProfileFieldValue,
} from '@hygge/shared';
import { CustomFieldRow } from './custom-field-row';
import { DeleteFieldDialog } from './delete-field-dialog';
import { FieldConfigDialog } from './field-config-dialog';

interface CustomBlockCardProps {
  block: EmployeeProfileBlock;
  employeeId: string;
  values: Map<string, EmployeeProfileFieldValue>;
  canCustomize: boolean;
  canEditValue: boolean;
  onRename: () => void;
  onDelete: () => void;
}

function extractApiError(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ message?: string | string[] }> | undefined;
  const data = axiosError?.response?.data;
  if (data?.message) {
    return Array.isArray(data.message) ? data.message.join(', ') : data.message;
  }
  return fallback;
}

export function CustomBlockCard({
  block,
  employeeId,
  values,
  canCustomize,
  canEditValue,
  onRename,
  onDelete,
}: CustomBlockCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
    disabled: !canCustomize,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const { data: fields } = useEmployeeProfileBlockFields(block.id);
  const createField = useCreateEmployeeProfileBlockField(block.id);
  const updateField = useUpdateEmployeeProfileBlockField(block.id);
  const deleteField = useDeleteEmployeeProfileBlockField(block.id);
  const reorderFields = useReorderEmployeeProfileBlockFields(block.id);

  const [createFieldOpen, setCreateFieldOpen] = useState(false);
  const [editFieldTarget, setEditFieldTarget] = useState<EmployeeProfileBlockField | null>(null);
  const [deleteFieldTarget, setDeleteFieldTarget] = useState<EmployeeProfileBlockField | null>(null);

  const sortedFields = useMemo(
    () =>
      [...(fields ?? [])].sort(
        (a, b) =>
          a.position - b.position ||
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [fields],
  );

  const fieldSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleFieldDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = sortedFields.findIndex((f) => f.id === active.id);
      const newIndex = sortedFields.findIndex((f) => f.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(sortedFields, oldIndex, newIndex);
      reorderFields.mutate(
        { fieldIds: reordered.map((f) => f.id) },
        {
          onError: (err) => toast.error(extractApiError(err, 'Failed to reorder fields')),
        },
      );
    },
    [sortedFields, reorderFields],
  );

  const handleCreateField = (input: CreateEmployeeProfileBlockFieldInput) => {
    createField.mutate(input, {
      onSuccess: () => setCreateFieldOpen(false),
      onError: (err) => toast.error(extractApiError(err, 'Failed to add field')),
    });
  };

  const handleUpdateField = (input: CreateEmployeeProfileBlockFieldInput) => {
    if (!editFieldTarget) return;
    updateField.mutate(
      { fieldId: editFieldTarget.id, ...input },
      {
        onSuccess: () => setEditFieldTarget(null),
        onError: (err) => toast.error(extractApiError(err, 'Failed to update field')),
      },
    );
  };

  const handleDeleteField = () => {
    if (!deleteFieldTarget) return;
    deleteField.mutate(deleteFieldTarget.id, {
      onSuccess: () => setDeleteFieldTarget(null),
      onError: (err) => toast.error(extractApiError(err, 'Failed to delete field')),
    });
  };

  const hasFields = sortedFields.length > 0;

  return (
    <Card ref={setNodeRef} style={style} className="overflow-hidden">
      <CardHeader className="group flex flex-row items-center justify-between space-y-0 border-b border-border/50 bg-muted/30 px-5 py-3">
        <div className="flex items-center gap-2 min-w-0">
          {canCustomize && (
            <button
              type="button"
              aria-label="Drag to reorder"
              {...attributes}
              {...listeners}
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground',
                'opacity-40 group-hover:opacity-100 transition-opacity',
                'cursor-grab active:cursor-grabbing touch-none',
              )}
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
          )}
          <CardTitle className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground truncate">
            {block.name}
          </CardTitle>
        </div>

        {canCustomize && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Block actions"
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity focus-visible:opacity-100"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onSelect={onRename}>
                <Pencil className="mr-2 h-3.5 w-3.5" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive">
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </CardHeader>
      <CardContent className="p-4 space-y-2">
        {hasFields ? (
          <DndContext
            sensors={fieldSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleFieldDragEnd}
          >
            <SortableContext
              items={sortedFields.map((f) => f.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1.5">
                {sortedFields.map((field) => (
                  <CustomFieldRow
                    key={field.id}
                    employeeId={employeeId}
                    field={field}
                    value={values.get(field.id)?.value ?? null}
                    canCustomize={canCustomize}
                    canEditValue={canEditValue}
                    onEditField={() => setEditFieldTarget(field)}
                    onDeleteField={() => setDeleteFieldTarget(field)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            No fields yet. Add custom fields to this block to capture employee data.
          </p>
        )}

        {canCustomize && (
          <button
            type="button"
            onClick={() => setCreateFieldOpen(true)}
            className={cn(
              'flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border/70',
              'px-3 py-2 text-[11px] font-medium text-muted-foreground',
              'hover:border-foreground/40 hover:text-foreground hover:bg-muted/40 transition-colors',
            )}
          >
            <Plus className="h-3 w-3" />
            Add Field
          </button>
        )}
      </CardContent>

      <FieldConfigDialog
        open={createFieldOpen}
        mode="create"
        isSubmitting={createField.isPending}
        onOpenChange={setCreateFieldOpen}
        onSubmit={handleCreateField}
      />

      <FieldConfigDialog
        open={editFieldTarget !== null}
        mode="edit"
        field={editFieldTarget}
        isSubmitting={updateField.isPending}
        onOpenChange={(open) => {
          if (!open) setEditFieldTarget(null);
        }}
        onSubmit={handleUpdateField}
      />

      <DeleteFieldDialog
        open={deleteFieldTarget !== null}
        fieldName={deleteFieldTarget?.name ?? ''}
        isDeleting={deleteField.isPending}
        onOpenChange={(open) => {
          if (!open) setDeleteFieldTarget(null);
        }}
        onConfirm={handleDeleteField}
      />
    </Card>
  );
}
